import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom'
import { CoreOneRosterConfigurationCombinedTeam, CoreOneRosterConfigurationCombinedTeamSource, OneRosterCombineTeamListing } from "../../../../../../models/CoreOneRosterConfigurationCombinedTeam";
import { RequestState } from "../../../../../../models/RequestState";
import { GetOneRosterCombineTeamActivities, UpdateOneRosterCombineTeam } from "../../../../../../store/OneRoster/OneRosterCombineTeamAction";
import { FormGroup, Label, Input } from 'reactstrap';
import MultiSelectInput from '../../../../../misc/MultiSelectInput';
import StatusCode from '../../../../../../util/StatusCode';
import { SearchableOptionItem } from '../../../../../../util/UtilityTypes';
import ModalComponent from "../../../../../misc/ModalComponent";
import { useTranslation } from 'react-i18next';
import { RootState } from "../../../../../../store/store";


interface OneRosterCombineTeamActivitiesEditProps {
    isOpen: boolean
    editTeam: CoreOneRosterConfigurationCombinedTeam
    toggleModal: () => void
    success: (success: boolean) => void
    disabled: boolean
}

const validation = (team: CoreOneRosterConfigurationCombinedTeam) => {
    let valid: string[] = [];
    if (team.displayName === "") {
        valid.push('displayName')
    }
    if (team.courseTitle === "") {
        valid.push('courseTitle')
    }
    if (!team.sources.length) {
        valid.push('sources')
    }
    return valid;
}

const OneRosterCombineTeamActivitiesEdit: React.FC<OneRosterCombineTeamActivitiesEditProps> = (props) => {
    const { moduleId } = useParams();
    const dispatch = useDispatch();
    const [selectedActivities, setSelectedActvities] = useState<string[] | undefined>();
    const activities = useSelector<RootState, RequestState<OneRosterCombineTeamListing[]>>(state => state.onerosterCombineTeam.activities);
    const [team, setTeam] = useState<CoreOneRosterConfigurationCombinedTeam>(props.editTeam);
    const [status, setStatus] = useState<StatusCode>(StatusCode.NONE);
    const [savedActivities, setSavedActivities] = useState<OneRosterCombineTeamListing[]>([]);

    const { t } = useTranslation(['configuration', 'common']);
    const valid = validation(team);

    useEffect(() => {
        if (moduleId) {
            GetOneRosterCombineTeamActivities(moduleId)(dispatch);
            updateSelectedActivities();
        }
    }, [dispatch, moduleId]);

    const updateCombinedTeam = () => {
        if (!valid.length && moduleId) {
            setStatus(StatusCode.PENDING);
            UpdateOneRosterCombineTeam(moduleId, team)
                .then(x => {
                    setStatus(StatusCode.COMPLETE)
                    props.success(true)
                })
                .catch(error => {
                    setStatus(StatusCode.ERROR)
                })
        }
    }

    const updateSelectedActivities = () => {
        let sourceActivities: OneRosterCombineTeamListing[] = [];
        team.sources.map(x => {
            if (x.flowSyncActivityId !== undefined && x.flowSyncActivityTitle !== undefined) {
                sourceActivities.push({ id: x.flowSyncActivityId, title: x.flowSyncActivityTitle, schoolUnitTitle: x.schoolUnitTitle })
            }
        });
        setSavedActivities(sourceActivities);
        setSelectedActvities(sourceActivities.map(x => x.id));
    }

    const updateActivity = (name: string, value: string[] | null | undefined) => {
        if (value === null) {
            setSelectedActvities(undefined);
            return;
        }
        setSelectedActvities(value)

        if (value !== undefined) {
            let sources: CoreOneRosterConfigurationCombinedTeamSource[] = [];
            value.map(x => {
                sources.push({ "flowSyncActivityId": x })
            })
            setTeam({ ...team, "sources": sources });
        }
    }

    const updateTeam = (name: string, value: undefined | string) => {
        setTeam({ ...team, [name]: value });
    }

    const mapActivities = () => {
        let activityOptions: SearchableOptionItem<string>[] = [];
        if (activities.code === StatusCode.COMPLETE) {
            let activityListings = activities.data;
            if (savedActivities.length > 0) {
                activityListings = [...activityListings, ...savedActivities];
            }
            activityOptions = activityListings
                .filter(x => savedActivities.some(y => y.id === x.id || x.id !== team.flowSyncActivityId))
                .map(x => ({ value: x.id, label: `${x.title}, ${x.schoolUnitTitle}` }))
        }
        return activityOptions;
    };

    return (<>
        <ModalComponent
            isOpen={props.isOpen}
            toggleModal={() => props.toggleModal()}
            header={t('oneRosterConfiguration.combineTeam.title', { ns: 'configuration' })}
            update={updateCombinedTeam}
            disabled={valid.length > 0 || props.disabled}
            confirm={t('action.edit', { ns: 'common' })}
            cancel={t('action.cancel', { ns: 'common' })}
            saveStatus={status}
        >

            <FormGroup>
                <Label for="displayName">{t('oneRosterConfiguration.combineTeam.table.displayName', { ns: 'configuration' })}</Label>
                <Input
                    type="text"
                    name="displayName"
                    id="displayName"
                    autoComplete="off"
                    value={team.displayName}
                    onChange={(e) => updateTeam("displayName", e.target.value)}
                />
            </FormGroup>
            <FormGroup>
                <Label for="courseTitle">{t('oneRosterConfiguration.combineTeam.table.courseTitle', { ns: 'configuration' })}</Label>
                <Input
                    type="text"
                    name="courseTitle"
                    id="courseTitle"
                    autoComplete="off"
                    value={team.courseTitle}
                    onChange={(e) => updateTeam("courseTitle", e.target.value)}
                />
            </FormGroup>

            <Label>{t('oneRosterConfiguration.combineTeam.table.owner', { ns: 'configuration' })}</Label>
            <p>{team.flowSyncActivityTitle}, {team.schoolUnitTitle}</p>

            <FormGroup>
                <Label>{t('oneRosterConfiguration.combineTeam.table.sources', { ns: 'configuration' })}</Label>
                <MultiSelectInput
                    items={mapActivities()}
                    onChange={(name, value) => updateActivity(name, value)}
                    name='sources'
                    selected={selectedActivities}
                    defaultText='Välj aktiviteter'
                    disabled={activities.code !== StatusCode.COMPLETE || team?.flowSyncActivityId === ""}
                />
            </FormGroup>
        </ModalComponent>
    </>)
}

export default OneRosterCombineTeamActivitiesEdit;
